import { BaseApi } from '@Features/Base/BaseApi/BaseApi'

export type StandaloneCatalogPayload = {
	page?: number
	perPage?: number
	query?: string
}

export type StandaloneCheckoutPayload = {
	standalone_course_id: number
	payment_method: 'wallet' | 'scratchCard' | 'free'
	code?: string
	promocode?: string
}

export type StartLecturePayload = {
	standalone_course_id: number
	lecture_id: number
}

export const standaloneCourseApi = BaseApi.enhanceEndpoints({
	addTagTypes: ['StandaloneCourses', 'StandaloneCourse', 'MyStandaloneCourses', 'StandaloneAccess', 'Profile'],
}).injectEndpoints({
	endpoints: builder => ({
		getStandaloneCourses: builder.query<ApiResponse<StandaloneCourse[]>, StandaloneCatalogPayload>({
			query: payload => ({
				url: '/standalone-courses',
				params: {
					page: payload.page ?? 1,
					perPage: payload.perPage ?? 12,
					...(payload.query ? { query: payload.query } : {}),
				},
			}),
			providesTags: ['StandaloneCourses'],
		}),
		getFeaturedCourses: builder.query<ApiResponse<StandaloneCourse[]>, void>({
			query: () => '/standalone-courses/featured',
			providesTags: ['StandaloneCourses'],
		}),
		getStandaloneCourseByLabel: builder.query<ApiResponse<StandaloneCourse>, string>({
			query: label => `/standalone-courses/details/${label}`,
			providesTags: (result, error, label) => [{ type: 'StandaloneCourse', id: label }],
		}),
		getMyStandaloneCourses: builder.query<ApiResponse<MyStandaloneCourseEnrollment[]>, StandaloneCatalogPayload>({
			query: payload => ({
				url: '/standalone-courses/mine',
				params: { page: payload.page ?? 1, perPage: payload.perPage ?? 12 },
			}),
			providesTags: ['MyStandaloneCourses'],
		}),
		getStandaloneAccess: builder.query<ApiResponse<StandaloneAccessStatus>, number>({
			query: id => `/standalone-courses/${id}/access`,
			providesTags: (result, error, id) => [{ type: 'StandaloneAccess', id }],
		}),
		startStandaloneLecture: builder.mutation<ApiResponse<CourseEnrollmentPivot>, StartLecturePayload>({
			query: payload => ({
				url: `/standalone-courses/${payload.standalone_course_id}/lectures/${payload.lecture_id}/start`,
				method: 'POST',
			}),
			invalidatesTags: ['StandaloneCourse', 'StandaloneAccess'],
		}),
		checkoutStandaloneCourse: builder.mutation<ApiResponse<{ standalone_course_id: number; purchase_method: string; activate_until: string | null }>, StandaloneCheckoutPayload>({
			query: payload => ({
				url: '/checkout/standalone-course',
				method: 'POST',
				body: payload,
			}),
			invalidatesTags: ['StandaloneCourse', 'StandaloneCourses', 'MyStandaloneCourses', 'StandaloneAccess', 'Profile'],
		}),
		verifyStandalonePromocode: builder.query<ApiResponse<{ value: number }>, { standalone_course_id: number; code: string }>({
			query: payload => ({
				url: '/standalone-courses/promocodes/verify',
				method: 'POST',
				body: payload,
			}),
		}),
		getStandaloneInvoices: builder.query<ApiResponse<StandaloneInvoiceRow[]>, StandaloneCatalogPayload>({
			query: payload => ({
				url: '/standalone-courses/invoices',
				params: { page: payload.page ?? 1, perPage: payload.perPage ?? 10 },
			}),
			providesTags: ['MyStandaloneCourses'],
		}),
		getStandaloneInvoice: builder.query<ApiResponse<StandaloneInvoiceRow>, number>({
			query: id => `/standalone-courses/invoices/${id}`,
		}),
	}),
})

export const {
	useGetStandaloneCoursesQuery,
	useLazyGetStandaloneCoursesQuery,
	useGetFeaturedCoursesQuery,
	useGetStandaloneCourseByLabelQuery,
	useLazyGetStandaloneCourseByLabelQuery,
	useGetMyStandaloneCoursesQuery,
	useGetStandaloneAccessQuery,
	useStartStandaloneLectureMutation,
	useCheckoutStandaloneCourseMutation,
	useLazyVerifyStandalonePromocodeQuery,
	useGetStandaloneInvoicesQuery,
	useGetStandaloneInvoiceQuery,
} = standaloneCourseApi
